import React from 'react';

import useMountAnimation from '../hooks/useMountAnimation';
import { PAGE_NAV } from '../theme/tokens';
import { LayerRole, NavDir, TransitionHost, TransitionLayer } from './PageTransition.styles';

/**
 * One view layer — same enter/exit choreography as the route-level transition,
 * but without the per-route scroll restoration (an in-page swap keeps the same
 * URL, so there is nothing route-scoped to restore).
 */
const Layer = ({
  variant,
  dir,
  animate,
  settled,
  children,
}: {
  variant: LayerRole;
  dir: NavDir;
  /** When false the view renders at rest (no enter animation) — used for the very
   *  first mount, so the content doesn't animate on top of the page's own entrance. */
  animate: boolean;
  /** No swap is sliding: the view sits at rest, so it carries no slide-only styling. */
  settled: boolean;
  children: React.ReactNode;
}): React.ReactElement => {
  const mounted = useMountAnimation();
  const entered = animate ? mounted : true;
  return (
    <TransitionLayer
      $role={variant}
      $dir={dir}
      $entered={entered}
      $settled={settled}
      aria-hidden={variant === 'exit' || undefined}
    >
      {children}
    </TransitionLayer>
  );
};

type SlideState = {
  active: 0 | 1;
  gen: [number, number];
  exitingSlot: 0 | 1 | null;
  exitNode: React.ReactNode;
  dir: NavDir;
  seq: number;
  token: string;
};

export type SlideSwapProps = {
  /** Identifies the view currently shown. When it changes, the swap animates. */
  token: string;
  /** Direction of the change — `forward` (in from the right), `back` (in from the
   *  left), or `none` (no slide, e.g. the very first render). */
  direction: NavDir;
  children: React.ReactNode;
};

/**
 * Horizontal slide between two in-page views, driven by a state `token` instead
 * of the URL — the same mechanism as the route-level `PageTransition`, reused for
 * swaps that happen *within* a single route (e.g. the Settings list opening a
 * section and coming back).
 *
 * On a `token` change the incoming view mounts into the free slot and slides in
 * from `direction`, while the previous view stays put as a frozen instance and
 * slides out the opposite way. Lives inside a stable page, so it always
 * reconciles in place — no remount handling needed.
 */
export const SlideSwap = ({ token, direction, children }: SlideSwapProps): React.ReactElement => {
  const prevChildrenRef = React.useRef<React.ReactNode>(children);

  const [state, setState] = React.useState<SlideState>(() => ({
    active: 0,
    gen: [0, -1],
    exitingSlot: null,
    exitNode: null,
    dir: direction,
    seq: 0,
    token,
  }));

  // Detect the view change during render so the incoming + frozen outgoing views
  // commit together (no flash before the slide).
  if (token !== state.token) {
    const oldActive = state.active;
    const newActive: 0 | 1 = oldActive === 0 ? 1 : 0;
    const seq = state.seq + 1;
    const gen: [number, number] = [...state.gen];
    gen[newActive] = seq;
    setState({
      active: newActive,
      gen,
      exitingSlot: oldActive,
      exitNode: prevChildrenRef.current,
      dir: direction,
      seq,
      token,
    });
  }

  React.useEffect(() => {
    prevChildrenRef.current = children;
  });

  React.useEffect(() => {
    if (state.exitingSlot == null) return undefined;
    const settledSeq = state.seq;
    const timer = window.setTimeout(() => {
      setState((s) => (s.seq === settledSeq ? { ...s, exitingSlot: null, exitNode: null } : s));
    }, PAGE_NAV.DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [state.seq, state.exitingSlot]);

  // Between swaps the view is at rest: no clip and no `will-change`, both of which only the slide needs. Kept, they cut
  // what a view draws past its edges and trap its pinned header under the page's header scrim (see the styles).
  const settled = state.exitingSlot == null;

  const renderSlot = (slot: 0 | 1): React.ReactElement | null => {
    if (slot === state.active) {
      // gen 0 is the initial mount (no swap yet) → render at rest, no animation.
      return (
        <Layer key={state.gen[slot]} variant="enter" dir={state.dir} animate={state.gen[slot] > 0} settled={settled}>
          {children}
        </Layer>
      );
    }
    if (slot === state.exitingSlot) {
      return (
        <Layer key={state.gen[slot]} variant="exit" dir={state.dir} animate settled={false}>
          {state.exitNode}
        </Layer>
      );
    }
    return null;
  };

  return (
    <TransitionHost $fill={false} $clip={!settled}>
      {renderSlot(0)}
      {renderSlot(1)}
    </TransitionHost>
  );
};

export default SlideSwap;
